import { Skeleton } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../common/Button';
import { Animal } from '../../../contexts/Staking/types';
import { useStack } from '../../../hooks/useStaking';
import { ButtonGroup, MultiplierBox, NftImg, NftRoot } from './styles';

interface Props {
  token: Animal;
  isStaked?: boolean;
}

const NftCard = ({ token, isStaked }: Props) => {
  const {
    getPendingStakingRewards,
    fetchAnimal,
    stakeAnimal,
    unstakeAnimal,
    claimStakingRewards,
  } = useStack();

  const [augmentedAnimal, setAugmentedAnimal] = useState<Animal>();
  const [stakingPeriod, setStakingPeriod] = useState<Date>(new Date());
  const [redeemable, setRedeemable] = useState<number>(0);

  const fetchAnimalStats = useCallback(async () => {
    setAugmentedAnimal(await fetchAnimal(token.mint));
  }, [token, fetchAnimal]);

  useEffect(() => {
    if (!token.lastClaim) fetchAnimalStats();
  }, [token, fetchAnimalStats]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (augmentedAnimal) setStakingPeriod(new Date());
    }, 500);
    return () => clearInterval(interval);
  }, [augmentedAnimal, getPendingStakingRewards, setStakingPeriod]);

  // handle Stake Nft
  const handleStake = useCallback(async () => {
    if (!augmentedAnimal) return;
    await stakeAnimal(augmentedAnimal);
  }, [augmentedAnimal, stakeAnimal]);

  // handle Unstake NFt
  const handleUnstake = useCallback(async () => {
    if (!augmentedAnimal) return;
    await unstakeAnimal(augmentedAnimal);
  }, [augmentedAnimal, unstakeAnimal]);

  // handle Claim Earning
  const handleClaim = useCallback(async () => {
    if (!augmentedAnimal) return;
    await claimStakingRewards(augmentedAnimal);
    fetchAnimalStats();
  }, [augmentedAnimal, claimStakingRewards, fetchAnimalStats]);

  // pending rewords
  useEffect(() => {
    if (!augmentedAnimal?.lastClaim || !stakingPeriod) return;
    if (augmentedAnimal?.lastClaim && isStaked) {
      const redeem = getPendingStakingRewards(augmentedAnimal, stakingPeriod);

      return () => {
        setRedeemable(redeem);
      };
    }
  }, [augmentedAnimal, stakingPeriod, getPendingStakingRewards]);
  return (
    <NftRoot>
      {token.uriData.image ? (
        <NftImg src={token.uriData.image} alt='' />
      ) : (
        <Skeleton.Avatar active shape='square' size={300} />
      )}

      <h3>{token.uriData.name}</h3>
      <MultiplierBox>
        <span>Rewards Per Day</span>{' '}
        <span>
          <span style={{ fontSize: 16 }}>x</span>
          {(token?.emissionsPerDay || 0) / 10 ** 9}
        </span>
      </MultiplierBox>
      {augmentedAnimal?.lastClaim && isStaked && (
        <MultiplierBox>
          <span>Pending Rewards</span> <span>{redeemable.toPrecision(5)}</span>
        </MultiplierBox>
      )}

      <ButtonGroup>
        {augmentedAnimal?.lastClaim && isStaked ? (
          <>
            <Button onClick={handleClaim} fixedWidth={true}>
              Claim Rewards
            </Button>
            <Button onClick={handleUnstake} fixedWidth={true}>
              Unstake Dolphin
            </Button>
          </>
        ) : (
          <Button onClick={handleStake} fixedWidth={true}>
            Stake Dolphin
          </Button>
        )}
      </ButtonGroup>
    </NftRoot>
  );
};

export default NftCard;
